import { useMemo, useState, useEffect } from 'react';
import { classifyRI } from '@leopart/calc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Calendar, Clock, MapPin, Users, X, CheckCircle, Circle,
  Dumbbell, Swords, ClipboardList, Activity, Heart, Edit3,
} from 'lucide-react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { EVENT_TYPES } from '../../../enums/event';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { IEventResult } from '../../../models/eventResult';
import { IUser2 } from '../../../models/user copy';
import { Paginated } from '../../../typings/Paginated';
import styles from './EventDetailDrawer.module.scss';

interface EventDetailDrawerProps {
  event: any | null;
  teamId: string;
  onClose: () => void;
}

const EVENT_TYPE_LABEL: Record<string, string> = {
  [EVENT_TYPES.WORKOUT]: 'Allenamento',
  [EVENT_TYPES.MATCH]: 'Gara',
  [EVENT_TYPES.TEST]: 'Test',
  [EVENT_TYPES.SYSTEM_TEST_EVENT]: 'Test',
};

const EVENT_BADGE_CLASS: Record<string, string> = {
  [EVENT_TYPES.WORKOUT]: 'badgeWorkout',
  [EVENT_TYPES.MATCH]: 'badgeMatch',
  [EVENT_TYPES.TEST]: 'badgeTest',
  [EVENT_TYPES.SYSTEM_TEST_EVENT]: 'badgeTest',
};

const EVENT_ICON: Record<string, React.ReactNode> = {
  [EVENT_TYPES.WORKOUT]: <Dumbbell size={12} />,
  [EVENT_TYPES.MATCH]: <Swords size={12} />,
  [EVENT_TYPES.TEST]: <ClipboardList size={12} />,
  [EVENT_TYPES.SYSTEM_TEST_EVENT]: <ClipboardList size={12} />,
};

function getEventName(event: any): string {
  return (
    event?.name ??
    event?.testType ??
    (typeof event?.testId === 'object' ? event?.testId?.name : null) ??
    '—'
  );
}

function formatDuration(event: any): string | null {
  if (!event?.startDate || !event?.endDate) return null;
  const diff = dayjs(event.endDate).diff(dayjs(event.startDate), 'minute');
  if (diff < 0) return null;
  if (diff < 60) return `${diff} min`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function getEventDuration(event: any): number {
  if (!event?.startDate || !event?.endDate) return 0;
  const diff = dayjs(event.endDate).diff(dayjs(event.startDate), 'minute');
  return diff > 0 ? diff : 0;
}

function getRiBadge(score: number | null | undefined): { label: string; cls: string } {
  if (score == null) return { label: '—', cls: styles.riNone };
  const label = `${Math.round(score)}`;
  // Soglie centralizzate in @leopart/calc (classifyRI).
  const clsByStatus = {
    critical: styles.riCritical,
    warning: styles.riWarning,
    normal: styles.riNormal,
    good: styles.riGood,
  } as const;
  return { label, cls: clsByStatus[classifyRI(score)] };
}

export default function EventDetailDrawer({ event, teamId, onClose }: EventDetailDrawerProps) {
  const endpoints = useEndpoints();
  const qc = useQueryClient();

  const isWorkout = event?.type === EVENT_TYPES.WORKOUT || event?.type === EVENT_TYPES.MATCH;
  const isTest = event?.type === EVENT_TYPES.TEST || event?.type === EVENT_TYPES.SYSTEM_TEST_EVENT;
  const eventDate = event?.date ?? event?.startDate;
  const eventDateStr = eventDate ? dayjs(eventDate).format('YYYY-MM-DD') : null;

  // Atleti del team
  const teamQ = useQuery<any>({
    queryKey: ['team-detail', teamId],
    queryFn: async () => {
      const res = await endpoints.getTeam(teamId);
      return res.data;
    },
    enabled: !!teamId && !!event,
  });

  // Risultati eventi associati a questo event
  const resultsQ = useQuery({
    queryKey: ['event-results', event?._id],
    queryFn: async () => {
      const res = await endpoints.getEventResults({ eventId: event?._id, limit: 200 });
      const docs = (res?.data as Paginated<IEventResult> | undefined)?.docs ?? (res?.data as unknown as IEventResult[] | undefined) ?? [];
      return Array.isArray(docs) ? docs : [];
    },
    enabled: !!event?._id,
  });

  // Atleti specifici dell'evento (può includere atleti non più nel team o eliminati)
  const eventUserIds = useMemo<string[]>(() => {
    const raw = (event?.userIds && Array.isArray(event.userIds) ? event.userIds : event?.users) ?? [];
    return raw.map((u: any) => (typeof u === 'object' ? String(u?._id ?? u?.id ?? '') : String(u))).filter(Boolean);
  }, [event]);

  const eventUsersQ = useQuery<any[]>({
    queryKey: ['event-users', eventUserIds.sort().join(',')],
    queryFn: async () => {
      if (eventUserIds.length === 0) return [];
      const res = await endpoints.getUsers({ _id: eventUserIds, limit: eventUserIds.length });
      const docs = (res?.data as Paginated<IUser2> | undefined)?.docs ?? (res?.data as unknown as IUser2[] | undefined) ?? [];
      return Array.isArray(docs) ? docs : [];
    },
    enabled: eventUserIds.length > 0,
  });

  // Lista atleti: prima prova i populate dall'evento, poi fetch dedicato, poi fallback team
  const athletes = useMemo(() => {
    const rawUsers = (event?.userIds && Array.isArray(event.userIds) ? event.userIds : event?.users) ?? [];
    if (!rawUsers.length) return [];
    // Se già popolati con firstName, ritorna così
    if (typeof rawUsers[0] === 'object' && (rawUsers[0]?.firstName || rawUsers[0]?.lastName)) {
      return rawUsers;
    }
    const fetched = eventUsersQ.data ?? [];
    const teamUsers: any[] = teamQ.data?.users ?? teamQ.data?.team?.users ?? [];
    const combined = new Map<string, any>();
    [...teamUsers, ...fetched].forEach((u) => {
      if (u?._id) combined.set(String(u._id), u);
    });
    return eventUserIds.map((id) => combined.get(id) ?? { _id: id });
  }, [event, eventUserIds, eventUsersQ.data, teamQ.data]);

  // Mappa risultati per atleta
  const resultsByAthlete = useMemo(() => {
    const map = new Map<string, any>();
    (resultsQ.data ?? []).forEach((r: any) => {
      const id = String(r.userId?._id ?? r.userId ?? r.user ?? '');
      if (id) map.set(id, r);
    });
    return map;
  }, [resultsQ.data]);

  // Readiness per atleti (solo per test)
  const readinessQs = useQuery<any[]>({
    queryKey: ['readiness-batch', event?._id, eventDateStr],
    queryFn: async () => {
      if (!eventDateStr) return [];
      const out = await Promise.all(
        athletes.map(async (a: any) => {
          const id = String(a?._id);
          if (!id) return null;
          try {
            const res = await endpoints.getReadiness(id, { date: eventDateStr });
            return { athleteId: id, data: (res as any)?.data };
          } catch {
            return { athleteId: id, data: null };
          }
        }),
      );
      return out.filter(Boolean);
    },
    enabled: isTest && athletes.length > 0 && !!eventDateStr,
  });

  const readinessByAthlete = useMemo(() => {
    const map = new Map<string, any>();
    (readinessQs.data ?? []).forEach((r: any) => map.set(r.athleteId, r.data));
    return map;
  }, [readinessQs.data]);

  // Aggregati workout
  const aggregates = useMemo(() => {
    if (!isWorkout) return null;
    const rpeResults = (resultsQ.data ?? []).filter((r: any) => r.type === 'rpe' && !r.skipped);
    const compiled = rpeResults.length;
    const total = athletes.length;
    // ATTENZIONE semantica DB: `value` = RPE 1-10, `rpeValue` = sRPE GIÀ
    // calcolato (value × minuti). Usare rpeValue come "RPE" produceva
    // media ~480 e sRPE = RPE × duration² (bug quadrato).
    const rawRpe = (r: any): number =>
      r.value ?? (r.rpeValue && r.duration ? r.rpeValue / r.duration : 0);
    const rpeSum = rpeResults.reduce((s: number, r: any) => s + rawRpe(r), 0);
    const srpeSum = rpeResults.reduce((s: number, r: any) => {
      const srpe =
        r.rpeValue ?? rawRpe(r) * (r.duration ?? getEventDuration(event));
      return s + (srpe || 0);
    }, 0);
    return {
      compiled, total,
      avgRpe: compiled > 0 ? rpeSum / compiled : 0,
      sRpeTotal: srpeSum,
      pctCompiled: total > 0 ? Math.round((compiled / total) * 100) : 0,
    };
  }, [isWorkout, resultsQ.data, athletes, event]);

  // Mutation update minutaggio
  const updateDurationMut = useMutation({
    mutationFn: ({ resultId, duration }: { resultId: string; duration: number }) =>
      endpoints.patchEventResult(resultId, { duration }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-results', event?._id] });
    },
  });

  // Local state per editing duration (per athleteId)
  const [editing, setEditing] = useState<Record<string, number>>({});
  useEffect(() => {
    setEditing({});
  }, [event?._id]);

  if (!event) return null;

  const total = athletes.length;
  const compiledIds = Array.isArray(event.compiledIds) ? event.compiledIds.map(String) : [];
  const isAthleteCompiled = (id: string) => compiledIds.includes(String(id))
    || resultsByAthlete.has(String(id));

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={`${styles.typeBadge} ${styles[EVENT_BADGE_CLASS[event.type]] ?? ''}`}>
              {EVENT_ICON[event.type]}
              {EVENT_TYPE_LABEL[event.type] ?? event.type}
            </span>
            <h2 className={styles.title}>{getEventName(event)}</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Chiudi">
            <X size={20} />
          </button>
        </div>

        {/* Info bar */}
        <div className={styles.infoBar}>
          <div className={styles.infoItem}>
            <Calendar size={14} className={styles.infoIcon} />
            <span>
              {event.startDate
                ? dayjs(event.startDate).format('DD MMM YYYY HH:mm')
                : eventDate
                ? dayjs(eventDate).format('DD MMM YYYY')
                : '—'}
              {event.endDate && <> → {dayjs(event.endDate).format('HH:mm')}</>}
            </span>
            {formatDuration(event) && (
              <span className={styles.infoMuted}>({formatDuration(event)})</span>
            )}
          </div>
          {event.location && (
            <div className={styles.infoItem}>
              <MapPin size={14} className={styles.infoIcon} />
              <span>{event.location}</span>
            </div>
          )}
          <div className={styles.infoItem}>
            <Users size={14} className={styles.infoIcon} />
            <span>{total} atlet{total !== 1 ? 'i' : 'a'}</span>
          </div>
        </div>

        {/* Aggregati */}
        {isWorkout && aggregates && (
          <div className={styles.aggregatesGrid}>
            <div className={styles.aggCard}>
              <div className={styles.aggLabel}>Compilazione</div>
              <div className={styles.aggValue}>
                {aggregates.compiled}/{aggregates.total}
              </div>
              <div className={styles.aggSub}>{aggregates.pctCompiled}%</div>
            </div>
            <div className={styles.aggCard}>
              <div className={styles.aggLabel}>Media RPE</div>
              <div className={styles.aggValue}>
                {aggregates.compiled > 0 ? aggregates.avgRpe.toFixed(1) : '—'}
              </div>
              <div className={styles.aggSub}>su 10</div>
            </div>
            <div className={styles.aggCard}>
              <div className={styles.aggLabel}>sRPE totale</div>
              <div className={styles.aggValue}>
                {aggregates.compiled > 0 ? Math.round(aggregates.sRpeTotal) : '—'}
              </div>
              <div className={styles.aggSub}>au · team</div>
            </div>
          </div>
        )}

        {isTest && (
          <div className={styles.aggregatesGrid}>
            <div className={styles.aggCard}>
              <div className={styles.aggLabel}>Compilazione</div>
              <div className={styles.aggValue}>
                {(resultsQ.data ?? []).filter((r: any) => !r.skipped).length}/{total}
              </div>
              <div className={styles.aggSub}>atleti con risultato</div>
            </div>
            <div className={styles.aggCard}>
              <div className={styles.aggLabel}>Tipo test</div>
              <div className={styles.aggValueSmall}>
                {event.testType ?? (typeof event.testId === 'object' ? event.testId?.name : '—')}
              </div>
            </div>
          </div>
        )}

        {/* Lista atleti */}
        <div className={styles.athletesSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              {isTest ? 'Risultati atleti' : 'Compilazione individuale'}
            </h3>
            {(resultsQ.isLoading || teamQ.isLoading) && (
              <span className={styles.sectionMuted}>Caricamento...</span>
            )}
          </div>

          {!resultsQ.isLoading && athletes.length === 0 && (
            <div className={styles.emptyAthletes}>Nessun atleta assegnato all'evento.</div>
          )}

          <div className={styles.athleteList}>
            {athletes.map((a: any) => {
              const id = String(a?._id);
              const result = resultsByAthlete.get(id);
              const compiled = isAthleteCompiled(id);
              const skipped = !!result?.skipped;
              // value = RPE 1-10; rpeValue = sRPE già moltiplicato (vedi sopra)
              const rpe =
                result?.value ??
                (result?.rpeValue && result?.duration
                  ? result.rpeValue / result.duration
                  : undefined);
              const localDuration = editing[id];
              const persistedDuration = result?.duration ?? getEventDuration(event);
              const currentDuration = localDuration ?? persistedDuration;
              const sRpe = rpe ? Math.round(rpe * currentDuration) : null;
              const readiness = readinessByAthlete.get(id);
              const ri = getRiBadge(readiness?.value ?? readiness?.interpolation);

              const hasName = !!(a?.firstName || a?.lastName);
              const isDeleted = !!a?.deleted;
              const displayName = hasName
                ? `${a?.firstName ?? ''} ${a?.lastName ?? ''}`.trim()
                : `Atleta #${id.slice(-6)}`;
              const initials = hasName
                ? `${(a?.firstName?.[0] ?? '').toUpperCase()}${(a?.lastName?.[0] ?? '').toUpperCase()}`
                : id.slice(-2).toUpperCase();

              return (
                <div key={id} className={styles.athleteRow}>
                  <div className={styles.athleteIdent}>
                    <div className={styles.athleteAvatar}>{initials || '·'}</div>
                    <div className={styles.athleteName}>
                      <div className={styles.athleteNamePrimary}>
                        {displayName}
                        {isDeleted && (
                          <span className={styles.deletedTag}> (eliminato)</span>
                        )}
                      </div>
                      <div className={styles.athleteNameSecondary}>
                        {compiled ? (
                          <span className={styles.statusCompiled}>
                            <CheckCircle size={11} /> Compilato
                          </span>
                        ) : (
                          <span className={styles.statusMissing}>
                            <Circle size={11} /> Non compilato
                          </span>
                        )}
                        {skipped && (
                          <span className={styles.statusSkipped}>· skip</span>
                        )}
                        {!hasName && (
                          <span className={styles.statusMuted}>· archiviato</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.athleteData}>
                    {isWorkout && (
                      <>
                        <div className={styles.metric}>
                          <span className={styles.metricLabel}>RPE</span>
                          <span className={styles.metricValue}>
                            {rpe != null ? rpe : '—'}
                          </span>
                        </div>
                        <div className={styles.metric}>
                          <span className={styles.metricLabel}>Min</span>
                          {result && !skipped ? (
                            <span className={styles.durationEdit}>
                              <input
                                type="number"
                                min={0}
                                value={localDuration ?? persistedDuration}
                                onChange={(e) =>
                                  setEditing((s) => ({ ...s, [id]: Number(e.target.value) || 0 }))
                                }
                                className={styles.durationInput}
                              />
                              {localDuration != null && localDuration !== persistedDuration && (
                                <button
                                  className={styles.durationSave}
                                  onClick={() =>
                                    updateDurationMut.mutate({
                                      resultId: result._id,
                                      duration: localDuration,
                                    })
                                  }
                                  disabled={updateDurationMut.isPending}
                                  title="Salva minutaggio"
                                >
                                  <Edit3 size={11} />
                                </button>
                              )}
                            </span>
                          ) : (
                            <span className={styles.metricValue}>
                              {persistedDuration > 0 ? persistedDuration : '—'}
                            </span>
                          )}
                        </div>
                        <div className={styles.metric}>
                          <span className={styles.metricLabel}>sRPE</span>
                          <span className={styles.metricValue}>{sRpe ?? '—'}</span>
                        </div>
                      </>
                    )}

                    {isTest && (
                      <>
                        <div className={styles.metric}>
                          <span className={styles.metricLabel}>Valore</span>
                          <span className={styles.metricValue}>
                            {(() => {
                              if (!result || skipped) return '—';
                              if (typeof result.value === 'number') return `${result.value}${result.unit ? ` ${result.unit}` : ''}`;
                              if (Array.isArray(result.cmjValue) && result.cmjValue.length > 0)
                                return `${result.cmjValue[result.cmjValue.length - 1]?.value}`;
                              if (Array.isArray(result.dropJumpValue) && result.dropJumpValue.length > 0)
                                return `${result.dropJumpValue[result.dropJumpValue.length - 1]?.value}`;
                              return '—';
                            })()}
                          </span>
                        </div>
                        <div className={styles.metric}>
                          <span className={styles.metricLabel}>
                            <Heart size={10} /> RI
                          </span>
                          <span className={`${styles.metricValue} ${styles.riChip} ${ri.cls}`}>
                            {ri.label}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Note */}
        {event.notes && (
          <div className={styles.notesBox}>
            <div className={styles.boxLabel}>Note</div>
            <div className={styles.boxValue}>{event.notes}</div>
          </div>
        )}

        {event.description && (
          <div className={styles.notesBox}>
            <div className={styles.boxLabel}>Descrizione</div>
            <div className={styles.boxValue}>{event.description}</div>
          </div>
        )}

        {event.workoutId && (
          <div className={styles.workoutLink}>
            <Activity size={14} /> Scheda allenamento collegata
          </div>
        )}

        {(resultsQ.isLoading || (isTest && readinessQs.isFetching)) && (
          <div className={styles.loadingOverlay}>
            <LeopartLoader />
          </div>
        )}
      </div>
    </div>
  );
}
